import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import TvOutlined from '@mui/icons-material/TvOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { nextStep, orderPhase, overdueDays, type OrderFeedRow } from '@zilian/contracts';
import { download } from '../api';
import OrderFeedTable from '../OrderFeedTable';
import { CountTabs, Heading, ListToolbar, Props, usePagination } from '../ui';

type View = 'ALL' | 'MINE' | 'LATE' | 'THEIRS' | 'MONEY' | 'DONE';

export function Orders(p: Props & { side: 'BUY' | 'SELL' }) {
  const purchasing = p.side === 'BUY';
  const label = purchasing ? '采购' : '销售';
  // A link may open the list narrowed, as 对账结算 does for one unit's orders to reconcile.
  const [params] = useSearchParams();
  const [view, setView] = useState<View>((params.get('view') as View) || 'ALL'),
    [search, setSearch] = useState(params.get('q') ?? '');

  // Each order's stage and turn is worked out once, then every filter and count reads it.
  const rows = useMemo(
    () =>
      (p.feed as OrderFeedRow[])
        .filter((o) => o.side === p.side)
        .map((order) => {
          const step = nextStep(order);
          return { order, step, phase: orderPhase(order), late: overdueDays(order, step) };
        }),
    [p.feed, p.side],
  );
  const inView = (r: (typeof rows)[number], v: View) =>
    v === 'ALL' ||
    (v === 'MINE'
      ? r.step.mine
      : v === 'LATE'
        ? r.late > 0
        : v === 'THEIRS'
          ? !r.step.mine && !r.step.finished
          : v === 'MONEY'
            ? // Delivered, not yet reconciled or fully received / paid: same rule as the dashboard.
              r.phase === 'money'
            : r.step.finished);
  const query = search.trim().toLowerCase();
  const matches = (r: (typeof rows)[number]) =>
    !query ||
    `${r.order.number}${r.order.peer}${r.order.sku_snapshot?.name ?? ''}`
      .toLowerCase()
      .includes(query);
  const count = (v: View) => rows.filter((r) => inView(r, v) && matches(r)).length;
  const list = rows.filter((r) => inView(r, view) && matches(r)).map((r) => r.order);
  const { pageItems, pager } = usePagination(list, `${view}|${query}`);

  const tabs: [View, string][] = [
    ['ALL', '全部'],
    ['MINE', '待我处理'],
    ['LATE', '已逾期'],
    ['THEIRS', '等待对方'],
    ['MONEY', '待对账收付'],
    ['DONE', '已结束'],
  ];
  return (
    <>
      <Heading
        title={`${label}管理`}
        subtitle={
          purchasing
            ? '采购订单、到货与入库。“待我处理”汇总了需要本企业确认、收货、入库或对账的单据。'
            : '销售订单、发货与出库。“待我处理”汇总了需要本企业确认、发货、出库或对账的单据。'
        }
        action={
          <>
            {/* The warehouse TV board: sales still to ship, opened in its own window. */}
            {!purchasing && (
              <Button
                variant="outlined"
                startIcon={<TvOutlined />}
                onClick={() => window.open('/board', 'picking-board')}
              >
                备货大屏
              </Button>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={() => p.newOrder(p.side)}>
              新建{label}
            </Button>
          </>
        }
      />
      <Paper>
        <ListToolbar>
          <CountTabs
            value={view}
            onChange={setView}
            items={tabs.map(([value, label]) => ({
              value,
              label,
              count: count(value),
              // Our turn is amber, overdue is red; other counts stay quiet.
              alert: value === 'MINE' ? true : value === 'LATE' ? 'error' : false,
            }))}
          />
          <Stack direction="row" spacing={1} sx={{ py: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder={`单号、${purchasing ? '供应商' : '客户'}或商品`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 230 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              startIcon={<DownloadOutlined />}
              onClick={() =>
                p.safe(() => download(`/export?kind=order-feed&side=${p.side}`, `${label}记录.csv`))
              }
            >
              导出
            </Button>
          </Stack>
        </ListToolbar>
        <OrderFeedTable
          orders={pageItems}
          postLocal={p.postLocal}
          quickStep={p.quickStep}
          openLocal={p.openLocal}
          side={p.side}
          fit
        />
        {/* The hint on the left, the pager (which shows the total) on the right. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minHeight: 52,
            borderTop: '1px solid #e7edef',
          }}
        >
          <Typography color="text.secondary" sx={{ flex: 1, px: 2, py: 1.5, fontSize: 12 }}>
            {!pager && `共 ${list.length} 笔 · `}
            左侧橙色标记的是待本企业处理的单据，点击任意一行查看详情
          </Typography>
          {pager}
        </Box>
      </Paper>
    </>
  );
}
